import { DigitalVisitReasonEnum } from "@/lib/__generated/api";
import type { NavigableStep, SchedulerContext } from "@/types/scheduler-machine";

import { isPatientUnder18 } from "./patient-age.util";

export type SchedulerFlow = "standard" | "motto" | "dentures";

export type WizardStep = NavigableStep | "confirmation";

type FlowContext = Pick<SchedulerContext, "initialReason" | "reasonForVisit">;

// Motto is URL-gated: only landing on /mottoaligners starts it, and picking Clear Aligners from the
// reason grid stays on the standard flow. Dentures follows its flow however the reason was picked.
export function getSchedulerFlow(context: FlowContext): SchedulerFlow {
  if (context.initialReason === DigitalVisitReasonEnum.ClearAligners) return "motto";
  if (context.reasonForVisit === DigitalVisitReasonEnum.Dentures) return "dentures";
  return "standard";
}

// Motto and dentures book the slot before collecting the patient's details.
export function isSlotFirstFlow(context: FlowContext): boolean {
  return getSchedulerFlow(context) !== "standard";
}

export function requiresNeedsBasedQuestions(context: FlowContext): boolean {
  return (
    getSchedulerFlow(context) !== "motto" &&
    context.reasonForVisit !== DigitalVisitReasonEnum.HealthyMouthMovement
  );
}

// The steps after reason for visit, in the order the wizard visits them. The machine's `onDone`
// transitions follow the same order; scheduler-flow.util.test walks the machine to keep them in sync.
export function getStepOrder(context: SchedulerContext): WizardStep[] {
  const detailSteps: WizardStep[] = isSlotFirstFlow(context)
    ? ["dateTimeSelection", "patientInformation"]
    : ["patientInformation", "dateTimeSelection"];

  return [
    ...detailSteps,
    ...(isPatientUnder18(context) ? (["guarantorInformation"] as const) : []),
    "appointmentReview",
    "confirmation",
  ];
}
